/** Maximum number of manager accounts supported by the system */
export const MAX_SUPPORTED_MANAGER_ACCOUNTS = 1000;

/** Maximum number of advertiser accounts supported by the system */
export const MAX_SUPPORTED_ADVERTISER_ACCOUNTS = 20000;

/** Maximum number of manager accounts that can be selected in a single report */
export const MAX_SELECTED_MANAGER_ACCOUNTS = 500;

/** Maximum number of advertiser accounts that can be selected in a single report */
export const MAX_SELECTED_ADVERTISER_ACCOUNTS = 1500;

/** Maximum total number of accounts (all types combined) that can be selected in a single report */
export const MAX_TOTAL_SELECTED_ACCOUNTS = 2000;

/** Maximum number of campaigns that can be selected in a single report */
export const MAX_SELECTED_CAMPAIGNS = 2000;

/** Initial number of child accounts to display before showing "Show more advertisers" button */
export const INITIAL_CHILD_ACCOUNTS_LIMIT = 50;

/** Initial number of root (parent) accounts to display before infinite scroll loads more */
export const INITIAL_ROOT_ACCOUNTS_LIMIT = 50;

/**
 * Threshold for determining when total account selection is considered high cardinality.
 * Used by selectIsOnlineReportAccountsHighCardinality selector.
 */
export const TOTAL_ACCOUNTS_HIGH_CARDINALITY_THRESHOLD = 700;

/** Threshold for determining when advertiser account selection is considered high cardinality */
export const ADVERTISER_ACCOUNTS_HIGH_CARDINALITY_THRESHOLD = 700;

/**
 * Configuration object that defines high cardinality thresholds and validation rules
 * for dimension selections in flexible reports.
 *
 * This configuration is used to prevent performance issues and system overload by
 * limiting the number and types of dimensions that can be selected simultaneously
 * in reports. The rules are enforced by the dimension cardinality selector.
 *
 * @description Defines three types of dimension cardinality constraints:
 * 1. **Unrestricted**: Categories that have no selection limits
 * 2. **Exclusive**: Categories with specific item count restrictions
 * 3. **Distinct**: Overall limits on number of different categories selected
 *
 * @example
 * ```typescript
 * // Usage in validation logic
 * const isTimeValid = selectedTimeCount <= DIMENSION_HIGH_CARDINALITY_THRESHOLDS.exclusive.time.limit;
 * const distinctCount = getDistinctCategoriesCount(selectedDimensions);
 * const isDistinctValid = distinctCount <= DIMENSION_HIGH_CARDINALITY_THRESHOLDS.distinct.distinctCategoriesLimit;
 * ```
 *
 * @see {@link selectIsOnlineReportDimensionsHighCardinality} - Primary consumer of these thresholds
 */
export const DIMENSION_HIGH_CARDINALITY_THRESHOLDS = {
  /**
   * Categories that have no selection limits and can be freely selected
   * without contributing to cardinality restrictions.
   *
   * @type {string[]}
   * @example ['levelOfDetail', 'currency']
   */
  unrestricted: ['levelOfDetail', 'currency'],

  /**
   * Categories with specific item count restrictions. Each category in this object
   * has a maximum number of dimensions that can be selected from that category.
   *
   * @type {Record<string, { limit: number }>}
   * @example
   * ```typescript
   * {
   *   time: { limit: 1 }  // Only 1 time dimension allowed
   * }
   * ```
   */
  exclusive: {
    /**
     * Time dimension category restriction.
     * Only 1 time-related dimension can be selected at a time to prevent
     * complex temporal aggregations that could impact query performance.
     */
    time: { limit: 1 }
  },

  /**
   * Configuration for limiting the number of distinct categories that can be
   * selected simultaneously, excluding certain special categories.
   *
   * @type {Object}
   * @property {number} distinctCategoriesLimit - Maximum number of distinct categories allowed
   * @property {string[]} excludeCategories - Categories that don't count toward the distinct limit
   */
  distinct: {
    /**
     * Maximum number of distinct dimension categories that can be selected
     * simultaneously (excluding categories in excludeCategories).
     *
     * @type {number}
     * @default 1
     */
    distinctCategoriesLimit: 1,

    /**
     * Categories that are excluded from the distinct categories count.
     * These categories can be selected without contributing to the
     * distinctCategoriesLimit restriction.
     *
     * @type {string[]}
     * @example ['levelOfDetail', 'time', 'currency']
     */
    excludeCategories: ['levelOfDetail', 'time', 'currency', 'targeting', 'reportConfiguration']
  }
};

/**
 * Maximum number of recurring subscriptions allowed per user.
 *
 * @description This limit prevents users from creating excessive numbers of recurring
 * subscriptions which could impact system performance and resource usage.
 *
 * @see https://code.amazon.com/packages/URCSubscriptionsService/blobs/mainline/--/src/main/java/com/amazon/urc/subscriptions/validators/RecurringSubscriptionsLimitValidator.java#L30
 */
export const MAX_ALLOWED_RECURRING_SUBSCRIPTIONS = 80;
